import { createElement } from '../../../core/dom/helpers.js';
import { createReorderControls } from '../../../core/dom/reorder-controls.js';
import { extractVideoId, buildEmbedUrl } from '../../../core/media/youtube-url.js';

/**
 * @typedef {import('../../../contracts/forms.js').VideoEntry} VideoEntry
 */

/**
 * ONE reusable video card — 16:9 YouTube embed preview + URL input + Title + Description +
 * corner × remove, with a drag handle for reorder. Sibling of `image-card.js` (same keyed-
 * callback / disabled / destroy contract); the difference is the editable URL field and the
 * iframe-from-id preview.
 *
 * Single source of truth = the URL input. The card derives the video id from the CURRENT
 * input value (never cached state), so an edit that invalidates a previously-valid URL
 * immediately clears the preview. The card reports the raw typed URL via `onUrlChange(key, url)`;
 * the driver re-derives id/link the same way. Title/Description edits report verbatim text.
 *
 * Security: the iframe `src` is set via the `src` PROPERTY from `buildEmbedUrl(validId)` — a
 * regex-validated 11-char id only. No user URL ever reaches the DOM as markup or as an src.
 *
 * Reorder is available three ways: drag the handle (mouse), or the keyboard/touch-friendly
 * Move up / Move down buttons. The buttons are the accessible path — drag/drop is weak on touch
 * and unusable by keyboard; `setMoveState` disables each at the list boundary.
 *
 * @param {Object} args
 * @param {string} args.key - Stable client key the callbacks echo back.
 * @param {VideoEntry} args.value
 * @param {(key: string, url: string) => void} args.onUrlChange
 * @param {(key: string, text: string) => void} args.onTitleChange
 * @param {(key: string, text: string) => void} args.onDescriptionChange
 * @param {(key: string) => void} args.onRemove
 * @param {(key: string) => void} args.onMoveUp
 * @param {(key: string) => void} args.onMoveDown
 * @returns {{ el: HTMLElement, handle: HTMLElement, setDisabled: (d: boolean) => void, setMoveState: (canUp: boolean, canDown: boolean) => void, focusMove: (preferUp: boolean) => void, destroy: () => void }}
 */
export function createVideoCard({ key, value, onUrlChange, onTitleChange, onDescriptionChange, onRemove, onMoveUp, onMoveDown }) {
  let disabled = false;

  const card = createElement('div', { class: 'video-card' });

  // Reorder controls: drag handle (mouse) + Move up/down buttons (keyboard/touch).
  const reorder = createReorderControls({ key, ariaNoun: 'video', orientation: 'row', onMoveUp, onMoveDown });
  const controls = reorder.el;
  // withHandle defaults true → the handle is always present (the card's contract stays non-null).
  const handle = /** @type {HTMLElement} */ (reorder.handle);

  // Preview column: a 16:9 responsive frame holding the embed iframe (when an id resolves).
  const previewWrap = createElement('div', { class: 'video-card__preview' });
  /** @type {HTMLIFrameElement | null} */
  let iframeEl = null;

  // Fields column: URL + Title + Description, plus an inline invalid-URL hint.
  const fields = createElement('div', { class: 'video-card__fields' });

  const urlId = `videocard-url-${key}`;
  const urlLabel = createElement('label', { class: 'video-card__label', for: urlId }, ['YouTube URL']);
  const urlInput = /** @type {HTMLInputElement} */ (createElement('input', {
    type: 'text', id: urlId, class: 'video-card__url', placeholder: 'https://www.youtube.com/watch?v=…',
  }));
  urlInput.value = value.youtube_link ?? '';

  const hintId = `videocard-hint-${key}`;
  const hint = createElement('div', { id: hintId, class: 'video-card__hint', role: 'alert', hidden: 'hidden' },
    ['Not a valid YouTube URL']);

  const titleId = `videocard-title-${key}`;
  const titleLabel = createElement('label', { class: 'video-card__label', for: titleId }, ['Title (optional)']);
  const titleInput = /** @type {HTMLInputElement} */ (createElement('input', {
    type: 'text', id: titleId, class: 'video-card__title', placeholder: 'Title',
  }));
  titleInput.value = value.title ?? '';

  const descId = `videocard-desc-${key}`;
  const descLabel = createElement('label', { class: 'video-card__label', for: descId }, ['Description (optional)']);
  const descInput = /** @type {HTMLTextAreaElement} */ (createElement('textarea', {
    id: descId, class: 'video-card__desc', rows: '2', placeholder: 'Description',
  }));
  descInput.value = value.description ?? '';

  // Corner × remove (top-right).
  const removeBtn = /** @type {HTMLButtonElement} */ (createElement('button', {
    type: 'button', class: 'video-card__remove', 'aria-label': 'Remove video',
  }, ['×']));

  /** Build/clear the preview iframe from the current URL input. @param {string} id */
  function renderPreview(id) {
    if (iframeEl) { iframeEl.remove(); iframeEl = null; }
    if (!id) return;
    iframeEl = /** @type {HTMLIFrameElement} */ (createElement('iframe', {
      class: 'video-card__iframe', allowfullscreen: 'allowfullscreen', loading: 'lazy',
    }));
    iframeEl.src = buildEmbedUrl(id);
    iframeEl.title = titleInput.value.trim() || 'YouTube video preview';
    previewWrap.appendChild(iframeEl);
  }

  /** Re-parse the current URL, refresh preview + invalid hint + aria-invalid. @returns {string} resolved id ('' if none) */
  function refreshFromUrl() {
    const id = extractVideoId(urlInput.value) ?? '';
    renderPreview(id);
    // Hint (+ aria-invalid) only when the user typed something non-blank that doesn't resolve.
    const invalid = urlInput.value.trim() !== '' && id === '';
    hint.hidden = !invalid;
    urlInput.setAttribute('aria-invalid', invalid ? 'true' : 'false');
    // Link the input to the hint for screen readers only while the hint is shown.
    if (invalid) urlInput.setAttribute('aria-describedby', hintId);
    else urlInput.removeAttribute('aria-describedby');
    return id;
  }

  urlInput.addEventListener('input', () => {
    if (disabled) return;
    refreshFromUrl();
    onUrlChange(key, urlInput.value);
  });

  titleInput.addEventListener('input', () => {
    if (disabled) return;
    if (iframeEl) iframeEl.title = titleInput.value.trim() || 'YouTube video preview'; // live title
    onTitleChange(key, titleInput.value);
  });

  descInput.addEventListener('input', () => {
    if (disabled) return;
    onDescriptionChange(key, descInput.value);
  });

  removeBtn.addEventListener('click', () => {
    if (disabled) return;
    onRemove(key);
  });

  fields.appendChild(urlLabel);
  fields.appendChild(urlInput);
  fields.appendChild(hint);
  fields.appendChild(titleLabel);
  fields.appendChild(titleInput);
  fields.appendChild(descLabel);
  fields.appendChild(descInput);

  card.appendChild(controls);
  card.appendChild(previewWrap);
  card.appendChild(fields);
  card.appendChild(removeBtn);

  // Seed the preview/hint from the initial value.
  refreshFromUrl();

  return {
    el: card,
    handle,
    /** @param {boolean} d */
    setDisabled(d) {
      disabled = d;
      urlInput.disabled = d;
      titleInput.disabled = d;
      descInput.disabled = d;
      removeBtn.disabled = d;
      reorder.setDisabled(d); // toggles handle draggable + move-button disabled
      card.classList.toggle('video-card--disabled', d);
    },
    /** @param {boolean} up @param {boolean} down — whether this card can move up / down (boundary state). */
    setMoveState: reorder.setMoveState,
    /** @param {boolean} preferUp — re-home focus onto a Move button after a reorder rebuilt the card. */
    focusMove: reorder.focusMove,
    destroy() {
      if (iframeEl) { iframeEl.remove(); iframeEl = null; }
      reorder.destroy();
      card.remove();
    },
  };
}
